<template>
  <div>
    <provisioning-status @refresh="load" />
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>SASEゲートウェイ向けパケットフィルタリング一覧</span>
        <manual-button path="/sasegateway-packet-filtering-list/" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-row no-gutters>
          <v-col>
            <app-search
              :search="saseGatewayPackets.search"
              v-model="saseGatewayPackets.filter"
            />
          </v-col>
        </v-row>
        <div class="d-flex flex-row justify-end mb-2">
          <v-btn color="primary" @click="load">一覧更新</v-btn>
        </div>
        <app-table
          v-model:table-sort-by="saseGatewayPackets.sortBy"
          primary-key="wnumberAct"
          :fields="saseGatewayPackets.fields"
          :items="saseGatewayPackets.items"
          :filterOption="saseGatewayPackets.filter_option"
          :filter="saseGatewayPackets.filter"
          :hover="true"
          :fixed="true"
          sticky-header="50vh"
          @click:row="showSaseGatewayPktRules"
          :td-class="cellClass"
        >
          <template #cell(saseGatewayServiceType)="data">
            {{
              $filter.enumTo(
                data.item.saseGatewayServiceType,
                "saseGatewayServiceType"
              )
            }}
          </template>
        </app-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { SearchOption } from "@/components/AppSearch.vue";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import type { SaseGatewayPktFilterEntity } from "@/apis/PacketFilteringApi";
import PacketFilteringReference from "@/modals/packetFiltering/PacketFilteringReference.vue";

/** ゲートウェイタイプ（検索窓選択肢用） */
export const gatewayTypeItems = [{ value: "PALOALTO", text: "Palo Alto" }];

/** パケットフィルタリング設定有無（検索窓選択肢用） */
export const statusItems = [
  { value: "FILTERING", text: "有り" },
  { value: "NO_FILTERING", text: "無し" },
];

export default defineComponent({
  name: "SaseGatewayPacketFilteringList",
  data() {
    return {
      isLoaded: false,
      statusItems: statusItems,
      saseGatewayPackets: {
        sortBy: {
          key: "wnumberAct",
          order: "desc",
        } as SortItem,
        fields: [
          {
            key: "dstVpnVnCode",
            width: "240px",
            title: "接続先VPN/VNコード",
            sortable: true,
            filterByFormatted: true,
            formatter: (
              _v: string,
              _k: string,
              item: SaseGatewayPktFilterEntity
            ) => {
              return item.vnName
                ? `${item.vnName}(${item.dstVpnVnCode})`
                : item.dstVpnVnCode;
            },
          },
          {
            key: "vnConnectNameAct",
            width: "210px",
            title: "VNコネクト名(Act/Sby)",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (
              _v: string,
              _k: string,
              item: SaseGatewayPktFilterEntity
            ) => {
              return `${item.vnConnectNameAct}\n${item.vnConnectNameSby}`;
            },
          },
          {
            key: "wnumberAct",
            title: `VNコネクト番号(Act/Sby)`,
            width: "200px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (
              _v: string,
              _k: string,
              item: SaseGatewayPktFilterEntity
            ) => {
              return `${item.wnumberAct}\n${item.wnumberSby}`;
            },
          },
          {
            key: "saseGatewayServiceType",
            width: "160px",
            title: "タイプ",
            sortable: true,
          },
          {
            key: "filterringSettingStatus",
            width: "240px",
            title: "パケットフィルタリング設定有無",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: false,
            formatter: (value: string) =>
              this.$filter.enumTo(value, "filterringSettingStatus"),
          },
        ] as AppTableHeader<SaseGatewayPktFilterEntity>[],
        items: [] as SaseGatewayPktFilterEntity[],
        search: {
          dstVpnVnCode: { type: "text", label: "接続先VPN/VNコード" },
          vnConnectNameAct: { type: "text", label: "VNコネクト名" },
          wnumberAct: { type: "text", label: "VNコネクト番号" },
          saseGatewayServiceType: {
            type: "select",
            label: "タイプ",
            items: gatewayTypeItems,
          },
          filterringSettingStatus: {
            type: "select",
            label: "パケットフィルタリング設定有無",
            items: statusItems,
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          dstVpnVnCode: "like",
          vnConnectNameAct: "like",
          wnumberAct: "like",
          saseGatewayServiceType: "like",
          filterringSettingStatus: "equal",
        } as FilterOption,
      },
      saseGatewayPacketFilterKeyId: "",
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    /**
     * SASEゲートウェイ向けパケットフィルタリング一覧を取得
     */
    async load() {
      const saseGatewayData =
        await this.$api.packetFilter.getSaseGatewayVnConnects();
      this.saseGatewayPackets.items = saseGatewayData.packetFilterList;
      this.saseGatewayPacketFilterKeyId =
        saseGatewayData.saseGatewayPacketFilterKeyId;
    },
    /**
     * パケットフィルタルール表示
     * @param item 選択された行データ
     */
    async showSaseGatewayPktRules(item: SaseGatewayPktFilterEntity) {
      const data = (
        await this.$api.packetFilter.getSaseGatewayPktFilter(item.wnumberAct)
      ).packetFilterRuleList;
      const downloadUrl = this.$api.packetFilter.getSaseGatewayCsvURL();

      await this.$modal.show(PacketFilteringReference, {
        packetFilteringItem: data,
        clickRowSaseGatewayItem: item,
        packetFilterKeyId: this.saseGatewayPacketFilterKeyId,
        downloadUrl: downloadUrl,
      });
      await this.load();
    },
    /**
     * tdにクラスをつける
     * @param field テーブルヘッダー情報
     */
    cellClass(field: AppTableHeader<unknown>): string[] | undefined {
      if (
        field.key === "wnumberAct" ||
        field.key === "vnConnectNameAct" ||
        field.key === "dstVpnVnCode"
      ) {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
  },
});
</script>
